import type { Locale } from "date-fns";
import { de, enUS, fr, nb, sv } from "date-fns/locale";
import { getLangToDisplay, strings } from "@/I18n";
import { normalizeDate } from "@/v2/hooks/useFormatters";

// App locale code -> date-fns Locale. Norwegian ("no") maps to Bokmål ("nb").
// Anything unmapped falls back to US English, matching i18n's defaultLocale.
const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  de,
  fr,
  no: nb,
  sv,
};

// Resolved at call time (not import time) so it tracks the active locale, which
// can change after boot when the user switches languages. Used by react-day-picker
// (weekday headers, ARIA labels, week start) and date-fns format() display strings.
export function dateFnsLocale(): Locale {
  return DATE_FNS_LOCALES[getLangToDisplay()] ?? enUS;
}

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

const DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
  ...DATE_OPTS,
  hour: "2-digit",
  minute: "2-digit",
};

function format(
  value: string | undefined | null,
  opts: Intl.DateTimeFormatOptions
): string {
  if (!value) return strings("common.never");
  // Route through the canonical normalizeDate so an offset-less backend
  // timestamp is read as UTC, not browser-local — same guarantee the rest of
  // the app gets from useFormatters, and the reason the LISA-333 lint rule
  // bans a bare parseISO/new Date() here.
  const date = normalizeDate(value);
  if (Number.isNaN(date.getTime())) return strings("common.never");
  // Passing the app locale (not undefined) is the whole point: undefined would
  // format in the host's locale, leaving German users with English dates.
  return new Intl.DateTimeFormat(getLangToDisplay(), opts).format(date);
}

export function formatDate(value: string | undefined | null): string {
  return format(value, DATE_OPTS);
}

export function formatDateTime(value: string | undefined | null): string {
  return format(value, DATE_TIME_OPTS);
}
